import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Separator } from "../components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { 
  ArrowLeft,
  Calendar,
  Clock,
  CheckCircle2,
  Bell,
  Mail,
  Save,
  Pencil,
  AlertTriangle
} from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "../utils/api";
import AlertRichiamoDialog from "../components/AlertRichiamoDialog";
import { etichettaTask } from "../utils/etichettaTask";

const TASK_TYPE_LABELS = {
  call_strategica: "Call Strategica con Cliente",
  script_videomaker: "Script al Videomaker",
  script_cliente: "Script al Cliente",
  conferma_script: "Conferma Script Definitiva",
  ped_programmazione: "PED + Programmazione",
  avvio_adv: "Avvio Sponsorizzate / ADV",
  appuntamento_successivo: "Appuntamento Mese Successivo",
  report_fine_mese: "Report di Fine Mese",
  compilare_statistiche_adv: "Compilare Statistiche ADV",
  editing_video: "Editing Video",
  allineamento_strategico_tiktok: "Allineamento Strategico (TikTok)",
  call_settimanale_tiktok: "Call Settimanale (TikTok)",
  check_adv_tiktok: "Check ADV (TikTok)",
  check_affiliazioni_live_tiktok: "Check Affiliazioni e Live (TikTok)"
};

export default function TaskDetailPage({ user }) {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [editCallDateOpen, setEditCallDateOpen] = useState(false);
  const [newCallDate, setNewCallDate] = useState("");
  const [savingCallDate, setSavingCallDate] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);

  const isDirettore = user?.role === "direttore_tecnico" || user?.role === "direttore_commerciale";
  const isPM = user?.role === "pm" || isDirettore || (user?.secondary_roles || []).includes("pm");

  useEffect(() => {
    fetchTask();
  }, [taskId]);

  const fetchTask = async () => {
    try {
      const response = await apiFetch(`/tasks/${taskId}`);
      if (response.ok) {
        const data = await response.json();
        setTask(data);
        setNotes(data.notes || "");
        // Set initial call date for editing
        if (data.due_date) {
          const date = new Date(data.due_date);
          setNewCallDate(date.toISOString().split('T')[0]);
        }
      } else {
        toast.error("Task non trovato");
        navigate("/dashboard");
      }
    } catch (error) {
      console.error("Error fetching task:", error);
      toast.error("Errore nel caricamento del task");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      const response = await apiFetch(`/tasks/${taskId}/status?status=${newStatus}`, {
        method: "PATCH"
      });

      if (response.ok) {
        toast.success("Stato aggiornato");
        fetchTask();
      }
    } catch (error) {
      toast.error("Errore nell'aggiornamento dello stato");
    }
  };

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try {
      const response = await apiFetch(`/tasks/${taskId}/notes?notes=${encodeURIComponent(notes)}`, {
        method: "PATCH"
      });

      if (response.ok) {
        toast.success("Note salvate");
      }
    } catch (error) {
      toast.error("Errore nel salvataggio delle note");
    } finally {
      setSavingNotes(false);
    }
  };

  const handleSaveCallDate = async () => {
    if (!newCallDate) {
      toast.error("Seleziona una data valida");
      return;
    }

    setSavingCallDate(true);
    try {
      const response = await apiFetch(`/tasks/${taskId}/call-date`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ new_date: newCallDate })
      });

      const result = await response.json();

      if (response.ok) {
        toast.success("Data Call Strategica aggiornata");
        setEditCallDateOpen(false);
        fetchTask();
      } else {
        toast.error(result.detail || "Errore nell'aggiornamento");
      }
    } catch (error) {
      toast.error("Errore nell'aggiornamento della data");
    } finally {
      setSavingCallDate(false);
    }
  };

  const handleSendNotification = async (type) => {
    try {
      const token = localStorage.getItem("session_token");
      const headers = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;
      const response = await fetch(`${API}/notifications/send-email?task_id=${taskId}&notification_type=${type}`, {
        method: "POST",
        headers
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`Email inviata a ${result.recipients?.join(", ")}`);
        fetchTask();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nell'invio della notifica");
      }
    } catch (error) {
      toast.error("Errore nell'invio della notifica");
    }
  };

  const getSemaforoBadge = (semaforo, status) => {
    if (status === "completato") {
      return <Badge className="bg-slate-100 text-slate-600 border-slate-200">Completato</Badge>;
    }
    const styles = {
      verde: "badge-verde",
      giallo: "badge-giallo",
      rosso: "badge-rosso",
      scaduto: "badge-scaduto"
    };
    const labels = {
      verde: "In tempo",
      giallo: "Attenzione",
      rosso: "Oggi",
      scaduto: "Scaduto"
    };
    return <Badge className={`${styles[semaforo]} border`}>{labels[semaforo]}</Badge>;
  };

  // Check if task is call_strategica
  const isCallStrategica = task?.task_type === "call_strategica";
  
  // Get max date for call strategica (current due date)
  const getMaxCallDate = () => {
    if (!task?.due_date) return "";
    return new Date(task.due_date).toISOString().split('T')[0];
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-muted rounded w-48"></div>
        <div className="h-64 bg-muted rounded-xl"></div>
      </div>
    );
  }

  if (!task) {
    return null;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="task-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Indietro
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-foreground">
            {task.client_name}
          </h1>
          <p className="text-lg text-muted-foreground mt-1">
            {etichettaTask(task, TASK_TYPE_LABELS)}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAlertOpen(true)}
            className="border-red-300 text-red-700 hover:bg-red-50"
            data-testid="task-alert-btn"
          >
            <AlertTriangle className="w-4 h-4 mr-1" />
            Segnala Problema
          </Button>
          {getSemaforoBadge(task.semaforo, task.status)}
        </div>
      </div>

      <AlertRichiamoDialog
        open={alertOpen}
        onOpenChange={setAlertOpen}
        clientId={task.client_id}
        clientName={task.client_name}
        source="task"
        sourceRef={task.task_id}
        sourceLabel={`Task: ${etichettaTask(task, TASK_TYPE_LABELS)}`}
        prefillText={`Segnalo un problema sul task "${etichettaTask(task, TASK_TYPE_LABELS)}" (${task.client_name}):\n\n`}
      />

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="border border-slate-200">
            <CardHeader>
              <CardTitle className="font-heading">Dettagli Task</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-muted-foreground" />
                  <div className="flex-1">
                    <p className="text-sm text-muted-foreground">Scadenza</p>
                    <div className="flex items-center gap-2">
                      <p className="font-medium">
                        {new Date(task.due_date).toLocaleDateString("it-IT", {
                          weekday: "long",
                          day: "numeric",
                          month: "long",
                          year: "numeric"
                        })}
                      </p>
                      {isCallStrategica && task.status !== "completato" && (
                        <Dialog open={editCallDateOpen} onOpenChange={setEditCallDateOpen}>
                          <DialogTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                              <Pencil className="w-3 h-3 text-muted-foreground" />
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="sm:max-w-md">
                            <DialogHeader>
                              <DialogTitle className="font-heading">Modifica Data Call Strategica</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4">
                              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                <p className="text-sm text-amber-700 flex items-center gap-2">
                                  <AlertTriangle className="w-4 h-4" />
                                  La data può essere solo anticipata, non posticipata.
                                </p>
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="call-date">Nuova Data</Label>
                                <Input
                                  id="call-date"
                                  type="date"
                                  value={newCallDate}
                                  max={getMaxCallDate()}
                                  onChange={(e) => setNewCallDate(e.target.value)}
                                  data-testid="call-date-input"
                                />
                              </div>
                              <div className="flex gap-2 justify-end">
                                <Button variant="outline" onClick={() => setEditCallDateOpen(false)}>
                                  Annulla
                                </Button>
                                <Button
                                  onClick={handleSaveCallDate}
                                  disabled={savingCallDate}
                                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                                  data-testid="save-call-date-btn"
                                >
                                  {savingCallDate ? "Salvataggio..." : "Salva"}
                                </Button>
                              </div>
                            </div>
                          </DialogContent>
                        </Dialog>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-muted-foreground" />
                  <div>
                    {/* Le task «all'attivazione» partono dal pagamento, non dal girato,
                        o dall'ingresso nel team del ruolo a cui sono assegnate. */}
                    <p className="text-sm text-muted-foreground">
                      {task.pagamento_attivazione_id === "assegnazione_ruolo"
                        ? "Assegnazione al team"
                        : task.pagamento_attivazione_id ? "Attivazione pagata" : "Data Girato"}
                    </p>
                    <p className="font-medium">
                      {new Date(task.created_from_data_girato).toLocaleDateString("it-IT")}
                    </p>
                  </div>
                </div>
              </div>

              <Separator />

              <div>
                <p className="text-sm text-muted-foreground mb-2">Stato</p>
                <Select value={task.status} onValueChange={handleStatusChange}>
                  <SelectTrigger className="w-48" data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="da_fare">Da fare</SelectItem>
                    <SelectItem value="in_corso">In corso</SelectItem>
                    <SelectItem value="completato">Completato</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {task.completed_at && (
                <div className="flex items-center gap-2 text-sm text-emerald-600">
                  <CheckCircle2 className="w-4 h-4" />
                  Completato il {new Date(task.completed_at).toLocaleDateString("it-IT")}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          <Card className="border border-slate-200">
            <CardHeader>
              <CardTitle className="font-heading">Note</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Aggiungi note o link utili..."
                rows={4}
                data-testid="notes-textarea"
              />
              <Button
                onClick={handleSaveNotes}
                disabled={savingNotes}
                variant="outline"
                data-testid="save-notes-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {savingNotes ? "Salvataggio..." : "Salva Note"}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Actions */}
          {task.status !== "completato" && (
            <Card className="border border-slate-200">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Azioni Rapide</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {task.status === "da_fare" && (
                  <Button
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                    onClick={() => handleStatusChange("in_corso")}
                    data-testid="start-task-btn"
                  >
                    In Corso
                  </Button>
                )}
                <Button
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={() => handleStatusChange("completato")}
                  data-testid="complete-task-btn"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Segna Completato
                </Button>

                {isPM && (
                  <>
                    <Separator />
                    <p className="text-xs text-muted-foreground">Invia notifica manuale:</p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleSendNotification("giallo")}
                        className="flex-1"
                        data-testid="send-yellow-notification"
                      >
                        <Mail className="w-3 h-3 mr-1" />
                        Giallo
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleSendNotification("rosso")}
                        className="flex-1"
                        data-testid="send-red-notification"
                      >
                        <Mail className="w-3 h-3 mr-1" />
                        Rosso
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Notification History */}
          <Card className="border border-slate-200">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Bell className="w-4 h-4" />
                Storico Notifiche
              </CardTitle>
            </CardHeader>
            <CardContent>
              {task.notifications?.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Nessuna notifica inviata per questo task
                </p>
              ) : (
                <div className="space-y-3">
                  {task.notifications?.map((notif, index) => (
                    <div
                      key={notif.log_id || index}
                      className="p-3 border border-slate-200 rounded-lg text-sm"
                    >
                      <div className="flex items-center justify-between">
                        <Badge variant="secondary" className="text-xs">
                          {notif.channel === "email" ? "Email" : "Push"}
                        </Badge>
                        <Badge
                          className={notif.status === "success" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}
                        >
                          {notif.status === "success" ? "Inviata" : "Fallita"}
                        </Badge>
                      </div>
                      <p className="text-muted-foreground mt-2">
                        Tipo: {notif.notification_type}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(notif.sent_at).toLocaleString("it-IT")}
                      </p>
                      {notif.error && (
                        <p className="text-xs text-destructive mt-1">{notif.error}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
